// hooks/services/planningService.js

import { getAPI, postAPI, putAPI, deleteAPI } from '../../../apis/backend/apiNode';
import { paiementAPI as pythonPaiementAPI } from '../../../apis/backend/apiPython';

// ============================================
// CONSTANTS (moved from hook for reusability)
// ============================================

export const CHRONOLOGIE_TYPES = {
	NOTE: 'note',
	DEVIS: 'devis',
	DEMANDE_PRIX: 'demande_prix',
	BON_COMMANDE: 'bon_commande',
	FACTURE: "facture",
	APPEL: 'appel',
	EMAIL: 'email',
	RAPPEL: 'rappel',
};

export const INTERVENTION_TYPES = [
	{ value: 'devis', label: 'Devis' },
	{ value: 'intervention', label: 'Intervention' },
	{ value: 'pose', label: 'pose' },
	{ value: 'reclamation', label: 'reclamation' },
	{ value: 'reclamation_garantie', label: 'reclamation garantie' },
	{ value: 'deblocage', label: 'deblocage' },
	{ value: 'prendremesure', label: 'prendremesure' },
	{ value: 'enlevement', label: 'enlevement' },
];

export const INTERVENTION_MOTIFS = [
	{ value: 'rideaux', label: 'Rideaux' },
	{ value: 'volet', label: 'Volet roulant' },
	{ value: 'store', label: 'Store' },
	{ value: 'porte', label: 'Porte' },
	{ value: 'portail', label: 'Portail' },
	{ value: 'autre', label: 'Autre' },
];

export const INTERVENTION_STATUTS = [
	{ value: 'planifie', label: 'Planifié' },
	{ value: 'en_cours', label: 'En cours' },
	{ value: 'termine', label: 'Terminé' },
	{ value: 'annule', label: 'Annulé' },
];

export const CAR_STATUTS = {
	LOUE: 'louee',
	DISPONIBLE: 'disponible',
	PANNE: 'panne',
	ENTRETIEN: 'entretien',
};

// ============================================
// CLIENTS
// ============================================

export const loadClients = async () => {
	try {
		const clientsRes = await getAPI('store2000_client', '?limit=1000');
		if (clientsRes?.success) {
			return clientsRes.clients || [];
		}
		return [];
	} catch (err) {
		console.error('Load clients error:', err);
		return [];
	}
};

// ============================================
// TECHNICIENS
// ============================================

export const loadTechniciens = async () => {
	try {
		const response = await getAPI('employe', '/function/technicien');
		if (response?.success && Array.isArray(response.data)) {
			return response.data || [];
		} else if (Array.isArray(response)) {
			return response || [];
		}
		console.error('Invalid response format for techniciens:', response);
		return [];
	} catch (err) {
		console.error('Load techniciens error:', err);
		return [];
	}
};

// ============================================
// CARS
// ============================================

export const extractTechniciensFromCars = (carsList) => {
	const techniciensSet = new Set();
	carsList.forEach((car) => {
		if (car.conducteurs && Array.isArray(car.conducteurs)) {
			car.conducteurs.forEach((conducteur) => {
				if (conducteur.technicien && conducteur.technicien._id) {
					techniciensSet.add(
						JSON.stringify({
							_id: conducteur.technicien._id,
							name: conducteur.technicien.name,
							telephone: conducteur.technicien.telephone || '',
							email: conducteur.technicien.email || '',
						}),
					);
				}
			});
		}
	});
	return Array.from(techniciensSet).map((tech) => JSON.parse(tech));
};

export const loadCars = async (filters = {}, companies = []) => {
	try {
		const queryParams = new URLSearchParams();

		/*  if (filters.technicienFilter && filters.technicienFilter !== 'all') {
	  queryParams.append('technicienId', filters.technicienFilter);
	} */
		if (filters.carFilter && filters.carFilter !== 'all') {
			queryParams.append('carId', filters.carFilter);
		}
		if (filters.statutFilter && filters.statutFilter !== 'all') {
			queryParams.append('statut', filters.statutFilter);
		}
		if (companies && companies.length > 0) {
			const companyIds = companies.map((company) => company._id);
			if (companyIds.length > 0) {
				queryParams.append('companyIds', JSON.stringify(companyIds));
			}
		}

		const result = await getAPI('planning', `/car?${queryParams.toString()}`);
		if (result?.success) {
			const carsData = result.data.cars || result.data || [];
			return carsData;
		}
		console.error('Cars API error:', result);
		return [];
	} catch (err) {
		console.error('Load cars error:', err);
		return [];
	}
};

// ============================================
// INTERVENTIONS
// ============================================

export const transformInterventionsData = (apiData) => {
	if (!apiData || !Array.isArray(apiData)) return [];
	const interventions = [];
	apiData.forEach((carGroup) => {
		if (carGroup.interventions && Array.isArray(carGroup.interventions)) {
			carGroup.interventions.forEach((intervention) => {
				interventions.push({
					...intervention,
					car: carGroup.car?._id || carGroup._id,
					client: intervention.client || {},
					techniciens: intervention.techniciens || [],
				});
			});
		}
	});
	return interventions;
};

export const loadInterventions = async (filters = {}) => {
	try {
		const queryParams = new URLSearchParams();

		if (filters.reference && filters.reference.trim() !== '') {
			queryParams.append('reference', filters.reference.trim());
		}
		if (filters.typeFilter && filters.typeFilter !== 'all') {
			queryParams.append('type', filters.typeFilter);
		}
		if (filters.statutFilter && filters.statutFilter !== 'all') {
			queryParams.append('status', filters.statutFilter);
		}

		// FIX: Use date_debut and date_fin from filters
		if (filters.date_debut && filters.date_fin) {
			queryParams.append('date_debut', filters.date_debut.toISOString());
			queryParams.append('date_fin', filters.date_fin.toISOString());
		} else if (filters.viewMode === 'week') {
			// Fallback: calculate week if no dates provided
			const today = new Date();
			const startOfWeek = new Date(today);
			const dayOfWeek = startOfWeek.getDay();
			const diff = startOfWeek.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
			startOfWeek.setDate(diff);
			startOfWeek.setHours(0, 0, 0, 0);

			const endOfWeek = new Date(startOfWeek);
			endOfWeek.setDate(startOfWeek.getDate() + 6);
			endOfWeek.setHours(23, 59, 59, 999);

			queryParams.append('date_debut', startOfWeek.toISOString());
			queryParams.append('date_fin', endOfWeek.toISOString());
		} else {
			// Default: today
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			const tomorrow = new Date(today);
			tomorrow.setDate(today.getDate() + 1);
			tomorrow.setHours(23, 59, 59, 999);

			queryParams.append('date_debut', today.toISOString());
			queryParams.append('date_fin', tomorrow.toISOString());
		}

		// Add other filter parameters if needed
		if (filters.carFilter && filters.carFilter !== 'all') {
			queryParams.append('carId', filters.carFilter);
		}
		if (filters.technicienFilter && filters.technicienFilter !== 'all') {
			queryParams.append('technicianId', filters.technicienFilter);
		}

		console.log('🔍 API call to:', `/intervention/grouped-by-car?${queryParams.toString()}`);

		const result = await getAPI(
			'planning',
			`/intervention/grouped-by-car?${queryParams.toString()}`,
		);

		console.log('📦 API Response:', result);

		if (result?.success) {
			return transformInterventionsData(result.data);
		}
		console.error('Error loading interventions:', result);
		return [];
	} catch (err) {
		console.error('Load interventions error:', err);
		return [];
	}
};

export const getInterventionById = async (interventionId) => {
	if (!interventionId) return null;
	try {
		const result = await getAPI('planning', `/intervention/${interventionId}`);
		if (result?.success && result.data) {
			return result.data;
		}
		return null;
	} catch (error) {
		console.error('❌ Erreur getInterventionById:', error);
		return null;
	}
};

export const getInterventionByIdMobile = async (interventionId) => {
	if (!interventionId) return null;
	try {
		const result = await getAPI('planning', `/intervention/mobile/${interventionId}`);
		if (result?.success && result.data) {
			return result.data;
		}
		return null;
	} catch (error) {
		console.error('❌ Erreur getInterventionById:', error);
		return null;
	}
};

export const addIntervention = async (interventionData) => {
	
		const formattedData = {
			...interventionData,
			date_debut: new Date(interventionData.date_debut).toISOString(),
			date_fin: new Date(interventionData.date_fin).toISOString(),
		};
		const result = await postAPI('planning', formattedData, '/intervention');
		return result;
	
};

export const updateIntervention = async (interventionId, updatesWithUser) => {
	const result = await putAPI('planning', updatesWithUser, `/intervention/${interventionId}`);
	return result;
};

export const deleteIntervention = async (interventionId) => {
	const result = await deleteAPI('planning', `/intervention/${interventionId}`);
	return result?.success || false;
};

export const transferInterventions = async (payload) => {
	const result = await postAPI('planning', payload, '/intervention/transferer-interventions');
	return result?.success || false;
};

// ============================================
// CHRONOLOGIE
// ============================================

export const loadChronologieByIntervention = async (interventionId) => {
	try {
		const result = await getAPI('planning', `/chrono/intervention/${interventionId}`);
		return result?.success ? result.data : null;
	} catch (err) {
		console.error('Load chronologie error:', err);
		return null;
	}
};

export const addChronologieEntry = async (interventionId, entryData, userId) => {
	try {
		// Get or create chronologie
		let chronologieId;
		const chronologieResult = await getAPI(
			'planning',
			`/chrono/intervention/${interventionId}`,
		);
		if (chronologieResult?.success && chronologieResult.data) {
			chronologieId = chronologieResult.data._id;
		} else {
			const createResult = await postAPI('planning', { interventionId }, '/chrono');
			if (!createResult?.success) throw new Error('Erreur création chronologie');
			chronologieId = createResult.data._id;
		}

		// Build entry payload
		const entryPayload = {
			type: entryData.type,
			note: entryData.note,
			createdBy: userId,
			piecesJointes: entryData.piecesJointes || [],
		};
		if (entryData.type === 'devis' && entryData.devisId)
			entryPayload.devisId = entryData.devisId;
		if (entryData.type === 'demande_prix' && entryData.demandePrixId)
			entryPayload.demandePrixId = entryData.demandePrixId;
		if (entryData.type === 'bon_commande' && entryData.bonCommandeId)
			entryPayload.bonCommandeId = entryData.bonCommandeId;
		if (entryData.type === 'facture' && entryData.facture)
			entryPayload.facture = entryData.facture;

		const result = await postAPI('planning', entryPayload, `/chrono/${chronologieId}/entries`);
		return result?.success ? result.data : null;
	} catch (err) {
		console.error('❌ Add chronologie entry error:', err);
		throw err;
	}
};

// ============================================
// RAPPORTS
// ============================================

export const loadRapports = async () => {
	try {
		const result = await getAPI('planning', '/rapport');
		return result?.success ? result.data || [] : [];
	} catch (err) {
		console.error('Load rapports error:', err);
		return [];
	}
};

export const createRapport = async (rapportData) => {
	const result = await postAPI('planning', rapportData, '/rapport');
	console.log('reuslt from planning service', result);
	return result;
};

export const updateRapport = async (rapportId, updates) => {
	const result = await putAPI('planning', updates, `/rapport/${rapportId}`);
	return result?.success ? result.data : null;
};

export const deleteRapport = async (rapportId) => {
	const result = await deleteAPI('planning', `/rapport/${rapportId}`);
	return result?.success || false;
};

export const getRapportByIntervention = async (interventionId) => {
	if (!interventionId) return null;
	try {
		const result = await getAPI('planning', `/rapport/intervention/${interventionId}`);
		return result?.success ? result.data : null;
	} catch (error) {
		console.error('❌ Erreur getRapportByIntervention:', error);
		return null;
	}
};

// ============================================
// PAIEMENTS – DUAL SYSTEM (Node + Python)
// ============================================

// --- Python Global Payment Handlers ---
export const createGlobalPaiement = async (paiementData) => {
	try {
		const globalPaiementData = {
			destination_journal_id: paiementData.destination_journal_id,
			amount: parseFloat(paiementData.montant),
			date_payment: paiementData.date_payment || new Date().toISOString().split('T')[0],
			currency: paiementData.currency || 'EUR',
			payment_type: 'inbound',
			partner_type: 'customer',
			partner_id: paiementData.partner_id,
			partner_name: paiementData.partner_name,
			company_id: paiementData.company_id,
			reference: paiementData.reference || '',
			notes: paiementData.notes || '',
		};

		const pythonResult = await pythonPaiementAPI.create(globalPaiementData);
		if (pythonResult?.id) {
			return { success: true, globalPaymentId: pythonResult.id, data: pythonResult };
		}
		throw new Error('Erreur création paiement global');
	} catch (err) {
		console.error('❌ Erreur création paiement global:', err);
		throw err;
	}
};

export const updateGlobalPaiement = async (paiementId, paiementData) => {
	try {
		const pythonResult = await pythonPaiementAPI.update(paiementId, paiementData);
		if (pythonResult?.id) {
			return { success: true, data: pythonResult };
		}
		throw new Error('Erreur mise à jour paiement global');
	} catch (err) {
		console.error('❌ Erreur mise à jour paiement global:', err);
		throw err;
	}
};

// --- Unified Payment Handlers ---
export const addPaiement = async (rapportId, paiementData, interventionData, userData) => {
	const nodePaiementData = {
		montant: parseFloat(paiementData.montant),
		methode_paiement: paiementData.methode_paiement,
		destination_journal_id: paiementData.destination_journal_id || null,
		date_paiement: paiementData.date_payment || new Date().toISOString(),
		reference: paiementData.reference || '',
		statut: paiementData.statut || 'en_attente',
		notes: paiementData.notes || '',
		currency: paiementData.currency || 'EUR',
		payment_type: paiementData.payment_type || 'inbound',
	};

	let globalPaymentId = null;

	// Create Python payment if journal selected
	if (paiementData.destination_journal_id) {
		try {
			const clientData = interventionData || {};
			const globalResult = await createGlobalPaiement({
				...paiementData,
				partner_id: paiementData.partner_id || clientData.client?._id,
				partner_name: paiementData.partner_name || clientData.client?.nom,
				company_id: userData?.company_id,
			});
			if (globalResult?.globalPaymentId) {
				globalPaymentId = globalResult.globalPaymentId;
				nodePaiementData.payment_id = globalPaymentId;
			}
		} catch (globalError) {
			console.warn(
				'⚠️ Échec création paiement global, continuation avec paiement local:',
				globalError,
			);
		}
	}

	// Always create in MongoDB
	const nodeResult = await postAPI(
		'planning',
		nodePaiementData,
		`/rapport/${rapportId}/paiements`,
	);
	if (nodeResult?.success) {
		return { ...nodeResult.data, global_payment_id: globalPaymentId };
	} else {
		// Rollback Python if MongoDB fails
		if (globalPaymentId) {
			try {
				await deleteAPI('paiements', `/${globalPaymentId}`);
			} catch (rollbackError) {
				console.error('❌ Erreur annulation paiement global:', rollbackError);
			}
		}
		throw new Error("Erreur lors de l'ajout du paiement dans MongoDB");
	}
};

export const updatePaiement = async (
	rapportId,
	paiementId,
	updates,
	rapports,
	updateGlobalPaiement,
) => {
	const rapport = rapports.find((r) => r._id === rapportId);
	const paiementActuel = rapport?.paiements?.find((p) => p._id === paiementId);
	if (!rapport) throw new Error('Rapport non trouvé');

	const nodeUpdateData = {};
	const mongoFields = ['montant', 'methode_paiement', 'reference', 'statut', 'notes'];
	mongoFields.forEach((field) => {
		if (updates[field] !== undefined) nodeUpdateData[field] = updates[field];
	});
	if (updates.date_payment !== undefined) nodeUpdateData.date_paiement = updates.date_payment;
	if (updates.destination_journal_id !== undefined)
		nodeUpdateData.destination_journal_id = updates.destination_journal_id;

	const nodeResult = await putAPI(
		'planning',
		nodeUpdateData,
		`/rapport/${rapportId}/paiements/${paiementId}`,
	);
	if (!nodeResult?.success) throw new Error(nodeResult?.error || 'Erreur mise à jour paiement');

	// Update Python if needed
	if (paiementActuel?.payment_id && updates) {
		const globalUpdateData = {};
		if (updates.montant !== undefined) globalUpdateData.amount = updates.montant;
		if (updates.date_payment !== undefined)
			globalUpdateData.date_payment = updates.date_payment;
		if (updates.destination_journal_id !== undefined)
			globalUpdateData.destination_journal_id = updates.destination_journal_id;
		if (updates.reference !== undefined) globalUpdateData.reference = updates.reference;
		if (updates.notes !== undefined) globalUpdateData.notes = updates.notes;
		if (Object.keys(globalUpdateData).length > 0) {
			await updateGlobalPaiement(paiementActuel.payment_id, globalUpdateData);
		}
	}

	return nodeResult.data;
};

export const deletePaiement = async (rapportId, paiementId, rapports) => {
	const rapport = rapports.find((r) => r._id === rapportId);
	const paiement = rapport?.paiements?.find((p) => p._id === paiementId);
	const globalPaymentId = paiement?.payment_id;

	const nodeResult = await deleteAPI('planning', `/rapport/${rapportId}/paiements/${paiementId}`);
	if (!nodeResult?.success) throw new Error(nodeResult?.error || 'Erreur suppression paiement');

	if (globalPaymentId) {
		try {
			await deleteAPI('paiements', `/${globalPaymentId}`);
		} catch (globalError) {
			console.warn('⚠️ Échec suppression paiement global:', globalError);
		}
	}

	return true;
};

// Utility functions (kept here for consistency)
export const getPaiementsByRapport = async (rapportId) => {
	if (!rapportId) return [];
	const result = await getAPI('planning', `/rapport/${rapportId}/paiements`);
	return result?.success ? result.data : [];
};

export const getPaiementStats = async (rapportId) => {
	if (!rapportId) return null;
	const result = await getAPI('planning', `/rapport/${rapportId}/paiements/stats`);
	return result?.success ? result.data : null;
};

export const searchPaiements = async (filters = {}) => {
	const queryParams = new URLSearchParams(filters).toString();
	const result = await getAPI('planning', `/rapport/paiements/recherche?${queryParams}`);
	return result?.success ? result.data : [];
};

export const syncPaiements = async (
	rapportId,
	interventionData,
	rapports,
	createGlobalPaiement,
	userData,
) => {
	const rapport = rapports.find((r) => r._id === rapportId);
	if (!rapport?.paiements) return [];

	const syncResults = [];
	for (const paiement of rapport.paiements) {
		if (!paiement.payment_id && paiement.destination_journal_id) {
			try {
				const clientData = interventionData || {};
				const globalResult = await createGlobalPaiement({
					montant: paiement.montant,
					date_payment: paiement.date_paiement || new Date().toISOString(),
					destination_journal_id: paiement.destination_journal_id,
					currency: paiement.currency || 'EUR',
					reference: paiement.reference || '',
					notes: paiement.notes || '',
					partner_id: clientData.client?._id,
					partner_name: clientData.client?.nom,
					company_id: userData?.company_id,
				});
				if (globalResult?.globalPaymentId) {
					const updateResult = await putAPI(
						'planning',
						{ payment_id: globalResult.globalPaymentId },
						`/rapport/${rapportId}/paiements/${paiement._id}`,
					);
					if (updateResult?.success) {
						syncResults.push({
							paiementId: paiement._id,
							globalPaymentId: globalResult.globalPaymentId,
							status: 'synchronized',
						});
					}
				}
			} catch (error) {
				syncResults.push({
					paiementId: paiement._id,
					status: 'failed',
					error: error.message,
				});
			}
		}
	}
	return syncResults;
};

export const getGlobalPaiementDetails = async (globalPaymentId) => {
	if (!globalPaymentId) return null;
	const result = await getAPI('paiements', `/${globalPaymentId}`);
	return result?.success ? result.data : null;
};
